// Pages/admin/DetailsSubscriptionPage.jsx
import { useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  FiArrowLeft,
  FiEdit3,
  FiPackage,
  FiDollarSign,
  FiCalendar,
  FiUsers,
  FiToggleRight,
  FiToggleLeft,
  FiActivity,
  FiAlertCircle,
  FiLoader,
  FiStar,
  FiCheckCircle,
  FiXCircle,
} from "react-icons/fi";
import { BsClock, BsGraphUp, BsShieldCheck } from "react-icons/bs";
import { useSubscriptionPlanStore } from "../../store/subscriptionPlanStore";
import { HomeRepairServiceOutlined } from "@mui/icons-material";

export default function DetailsSubscriptionPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const {
    currentSubscription,
    loading,
    error,
    fetchSubscriptionById,
    toggleSubscriptionStatus,
    clearError,
    clearCurrentSubscription,
  } = useSubscriptionPlanStore();

  useEffect(() => {
    if (id) {
      fetchSubscriptionById(id);
    }
    return () => clearCurrentSubscription();
  }, [id, fetchSubscriptionById, clearCurrentSubscription]);

  const handleToggleStatus = async () => {
    try {
      await toggleSubscriptionStatus(id);
    } catch (error) {
      console.error("Erreur lors du changement de statut:", error);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "XAF",
    }).format(price);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("fr-FR", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading && !currentSubscription) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-secondary-600 mx-auto"></div>
          <p className="mt-4 font-nunito text-[14px] text-text font-medium">
            Chargement des détails...
          </p>
        </div>
      </div>
    );
  }

  if (!currentSubscription && !loading) {
    return (
      <div className="min-h-screen  flex items-center justify-center">
        <div className="text-center">
          <FiAlertCircle className="mx-auto text-red-500 mb-4" size={48} />
          <h2 className="font-poppins font-semibold text-[18px] text-title mb-2">
            Abonnement non trouvé
          </h2>
          <p className="font-nunito text-[14px] text-text mb-6">
            L'abonnement que vous recherchez n'existe pas.
          </p>
          <button
            onClick={() => navigate("/admin/subscriptions")}
            className="bg-secondary-500 hover:bg-secondary-600 text-white px-6 py-3  font-nunito font-semibold text-[14px]"
          >
            Retour aux abonnements
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <div className="max-w-4xl mx-auto">
        {/* En-tête */}
        <div className="mb-8">
          <button
            onClick={() => navigate("/admin/subscriptions")}
            className="bg-white mb-2 hover:bg-gray-50 text-gray-700 p-3  shadow-sm border border-gray-200 transition-all duration-200 hover:shadow-md"
          >
            <FiArrowLeft size={20} />
          </button>
          <div className="md:flex items-center justify-between">
            <div className="mb-4">
              <h1 className="font-poppins font-semibold text-[18px] text-title mb-2">
                <FiPackage
                  className="inline mr-4 text-secondary-600"
                  size={36}
                />
                Détails de l'abonnement
              </h1>
              <p className="font-nunito text-[14px] text-text text-lg">
                Informations complètes sur votre formule d'abonnement
              </p>
            </div>

            <div className="flex items-center justify-end space-x-3">
              <Link
                to={`/admin/subscriptions/${id}/edit`}
                className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white px-6 py-3  font-nunito font-semibold text-[14px] inline-flex items-center space-x-2 transition-all duration-200 hover:shadow-lg"
              >
                <FiEdit3 size={18} />
                <span>Modifier</span>
              </Link>
            </div>
          </div>
        </div>

        {/* Message d'erreur */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200  p-6">
            <div className="flex items-center space-x-3 text-red-800">
              <FiAlertCircle size={24} />
              <div>
                <span className="font-poppins font-medium text-[15px] text-subtitle">
                  {error}
                </span>
                <button
                  onClick={clearError}
                  className="ml-4 font-nunito text-[14px] text-red-600 hover:text-red-800 underline"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Colonne principale */}
          <div className="lg:col-span-2 space-y-6">
            {/* Carte d'informations générales */}
            <div className="bg-white  shadow-sm border border-gray-200 overflow-hidden">
              <div
                className={`p-2 md:p-6 border-b ${
                  currentSubscription.isActive
                    ? "bg-gradient-to-r from-green-50 to-emerald-50 border-green-200"
                    : "bg-gradient-to-r from-gray-50 to-slate-50 border-gray-200"
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2 md:space-x-4">
                    <div>
                      <h2 className="font-poppins font-semibold text-[18px] text-title">
                        {currentSubscription.name}
                      </h2>
                      <p className="font-nunito text-[14px] text-text mt-1 text-xs">
                        {currentSubscription.description}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={handleToggleStatus}
                    disabled={loading}
                    className={`p-3  transition-all duration-200 ${
                      currentSubscription.isActive
                        ? "text-green-600"
                        : "text-gray-400"
                    } ${loading ? "opacity-50 cursor-not-allowed" : ""}`}
                  >
                    {loading ? (
                      <FiLoader className="animate-spin" size={20} />
                    ) : currentSubscription.isActive ? (
                      <FiToggleRight size={20} />
                    ) : (
                      <FiToggleLeft size={20} />
                    )}
                  </button>
                </div>
              </div>

              <div className="p-2 md:p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="flex flex-col md:flex-row items-center justify-between p-4 bg-secondary-50 ">
                      <div className="flex items-center space-x-1 md:space-x-3">
                        <FiDollarSign
                          className="text-secondary-500"
                          size={20}
                        />
                        <span className="font-poppins font-medium text-[15px] text-subtitle">
                          Prix
                        </span>
                      </div>
                      <span className="font-poppins font-bold text-[16px] text-montant">
                        {formatPrice(currentSubscription.price)}
                      </span>
                    </div>

                    <div className="flex flex-col md:flex-row items-center justify-between p-4 bg-gray-50 ">
                      <div className="flex items-center space-x-1 md:space-x-3">
                        <BsShieldCheck className="text-gray-500" size={18} />
                        <span className="font-poppins font-medium text-[15px] text-subtitle">
                          Code
                        </span>
                      </div>
                      <code className="font-nunito text-[14px] text-text font-mono bg-white px-2 py-1 rounded">
                        {currentSubscription.code}
                      </code>
                    </div>

                    <div className="flex flex-col md:flex-row items-center justify-between p-4 bg-secondary-50 ">
                      <div className="flex items-center space-x-1 md:space-x-3">
                        <HomeRepairServiceOutlined
                          className="text-secondary-500"
                          size={20}
                        />
                        <span className="font-poppins font-medium text-[15px] text-subtitle">
                          Services
                        </span>
                      </div>
                      <span className="font-poppins font-bold text-[16px] text-montant">
                        {currentSubscription.includedServices?.length || 0}
                      </span>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div
                      className={`flex flex-col md:flex-row items-center justify-between p-2 md:p-4 ${
                        currentSubscription.isActive
                          ? "bg-green-50 border border-green-200"
                          : "bg-gray-50 border border-gray-200"
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        {currentSubscription.isActive ? (
                          <FiCheckCircle className="text-green-500" size={20} />
                        ) : (
                          <FiXCircle className="text-gray-400" size={20} />
                        )}
                        <span className="font-poppins font-medium text-[15px] text-subtitle">
                          Statut
                        </span>
                      </div>
                      <span
                        className={`px-3 py-1 font-nunito text-[14px] font-semibold ${
                          currentSubscription.isActive
                            ? "bg-green-100 text-green-800"
                            : "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {currentSubscription.isActive ? "Actif" : "Inactif"}
                      </span>
                    </div>

                    <div className="flex flex-col md:flex-row items-center justify-between p-4 bg-purple-50  border border-purple-200 ">
                      <div className="flex items-center space-x-1 md:space-x-3">
                        <BsClock className="text-purple-500" size={18} />
                        <span className="font-poppins font-medium text-[15px] text-subtitle">
                          Créé le
                        </span>
                      </div>
                      <span className="font-nunito text-[14px] text-text">
                        {formatDate(currentSubscription.created_at)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Carte des services inclus */}
            <div className="bg-white  shadow-sm border border-gray-200">
              <div className="p-2 md:p-6 border-b border-gray-200">
                <h3 className="font-poppins font-semibold text-[18px] text-title flex items-center">
                  <FiStar className="mr-3 text-yellow-500" size={24} />
                  Services inclus
                </h3>
                <p className="font-nunito text-[14px] text-text mt-1">
                  Liste des services et quantités inclus dans cet abonnement
                </p>
              </div>

              <div className="p-2 md:p-6">
                {currentSubscription.includedServices &&
                currentSubscription.includedServices.length > 0 ? (
                  <div className="space-y-4">
                    {currentSubscription.includedServices.map(
                      (includedService, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between p-4 bg-gray-50  hover:bg-gray-100 transition-all duration-200"
                        >
                          <div className="flex items-center space-x-4">
                            <div className="p-2 bg-white  shadow-sm">
                              <FiPackage
                                className="text-secondary-500"
                                size={18}
                              />
                            </div>
                            <div>
                              <h4 className="font-poppins font-semibold text-[18px] text-title">
                                {includedService.service?.name ||
                                  `Service #${includedService.id}`}
                              </h4>
                              {includedService.service?.description && (
                                <p className="font-nunito text-[14px] text-text mt-1">
                                  {includedService.service.description}
                                </p>
                              )}
                              {includedService.service?.price && (
                                <p className="font-poppins font-bold text-[16px] text-montant mt-1">
                                  {formatPrice(includedService.service.price)} /
                                  unité
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-secondary-800 px-3 py-1 font-nunito text-[14px] font-semibold">
                              × {includedService.quantity}
                            </div>
                            {includedService.service?.price && (
                              <div className="font-nunito text-[14px] text-text mt-2">
                                Total:{" "}
                                {formatPrice(
                                  includedService.service.price *
                                    includedService.quantity,
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      ),
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <FiPackage
                      className="mx-auto text-gray-400 mb-3"
                      size={48}
                    />
                    <h4 className="font-poppins font-semibold text-[18px] text-title mb-2">
                      Aucun service inclus
                    </h4>
                    <p className="font-nunito text-[14px] text-text">
                      Cet abonnement ne contient aucun service pour le moment.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
